(function () {
  const STORAGE_KEY = 'aviWishlist';
  const selectors = {
    button: '[data-avi-wishlist-button]',
    count: '[data-avi-wishlist-count]',
    page: '[data-avi-wishlist-page]',
    grid: '[data-avi-wishlist-grid]',
    empty: '[data-avi-wishlist-empty]',
    loading: '[data-avi-wishlist-loading]',
  };

  function normalizeHandle(handle) {
    return String(handle || '').trim();
  }

  function getWishlist() {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(parsed)) return [];
      return [...new Set(parsed.map(normalizeHandle).filter(Boolean))];
    } catch (error) {
      return [];
    }
  }

  function saveWishlist(handles) {
    const cleanHandles = [...new Set((handles || []).map(normalizeHandle).filter(Boolean))];
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cleanHandles));
    updateWishlistButtons();
    updateWishlistCount();
    window.dispatchEvent(new CustomEvent('avi:wishlist:updated', { detail: { handles: cleanHandles } }));
    return cleanHandles;
  }

  function addToWishlist(handle) {
    const cleanHandle = normalizeHandle(handle);
    if (!cleanHandle) return getWishlist();
    return saveWishlist([...getWishlist(), cleanHandle]);
  }

  function removeFromWishlist(handle) {
    const cleanHandle = normalizeHandle(handle);
    if (!cleanHandle) return getWishlist();
    return saveWishlist(getWishlist().filter((item) => item !== cleanHandle));
  }

  function isInWishlist(handle) {
    const cleanHandle = normalizeHandle(handle);
    return cleanHandle ? getWishlist().includes(cleanHandle) : false;
  }

  function toggleWishlist(handle) {
    return isInWishlist(handle) ? removeFromWishlist(handle) : addToWishlist(handle);
  }

  function updateWishlistButtons() {
    document.querySelectorAll(selectors.button).forEach((button) => {
      const handle = normalizeHandle(button.dataset.productHandle);
      const active = isInWishlist(handle);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
    });
  }

  function updateWishlistCount() {
    const count = getWishlist().length;
    document.querySelectorAll(selectors.count).forEach((element) => {
      element.textContent = count > 99 ? '99+' : String(count);
      element.hidden = count === 0;
    });
  }

  function formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    const currency = window.Shopify?.currency?.active || window.Shopify?.currency?.shopCurrency || 'USD';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
      }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  }

  function productCardMarkup(product) {
    const url = product.url || `/products/${product.handle}`;
    const image = product.featured_image
      ? `<img class="avi-wishlist-card__image" src="${product.featured_image}" alt="${escapeHtml(product.title)}" loading="lazy">`
      : '<div class="avi-wishlist-card__placeholder" aria-hidden="true"></div>';

    return `
      <article class="avi-wishlist-card" data-product-handle="${escapeHtml(product.handle)}">
        <a class="avi-wishlist-card__image-link" href="${url}">${image}</a>
        <div class="avi-wishlist-card__info">
          <h2 class="avi-wishlist-card__title"><a href="${url}">${escapeHtml(product.title)}</a></h2>
          <div class="avi-wishlist-card__price">${formatMoney(product.price)}</div>
        </div>
        <div class="avi-wishlist-card__actions">
          <a class="button button-secondary" href="${url}">View product</a>
          <button class="avi-wishlist-remove" type="button" data-avi-wishlist-remove data-product-handle="${escapeHtml(product.handle)}">Remove</button>
        </div>
      </article>
    `;
  }

  function escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value || '';
    return div.innerHTML;
  }

  async function fetchProduct(handle) {
    const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Product unavailable: ${handle}`);
    return response.json();
  }

  async function renderWishlistPage() {
    const page = document.querySelector(selectors.page);
    if (!page) return;

    const grid = page.querySelector(selectors.grid);
    const empty = page.querySelector(selectors.empty);
    const loading = page.querySelector(selectors.loading);
    const handles = getWishlist();

    if (!grid || !empty) return;

    grid.hidden = true;
    empty.hidden = true;
    if (loading) loading.hidden = false;

    if (handles.length === 0) {
      grid.innerHTML = '';
      empty.hidden = false;
      if (loading) loading.hidden = true;
      return;
    }

    const products = await Promise.all(
      handles.map((handle) =>
        fetchProduct(handle).catch(() => {
          return null;
        })
      )
    );
    const availableProducts = products.filter(Boolean);

    grid.innerHTML = availableProducts.map(productCardMarkup).join('');
    grid.hidden = availableProducts.length === 0;
    empty.hidden = availableProducts.length > 0;
    if (loading) loading.hidden = true;
  }

  function handleDocumentClick(event) {
    const wishlistButton = event.target.closest(selectors.button);
    if (wishlistButton) {
      event.preventDefault();
      event.stopPropagation();
      const handle = normalizeHandle(wishlistButton.dataset.productHandle);
      if (!handle) return;
      toggleWishlist(handle);
      if (document.querySelector(selectors.page)) renderWishlistPage();
      return;
    }

    const removeButton = event.target.closest('[data-avi-wishlist-remove]');
    if (removeButton) {
      event.preventDefault();
      event.stopPropagation();
      const handle = normalizeHandle(removeButton.dataset.productHandle);
      if (!handle) return;
      removeFromWishlist(handle);
      renderWishlistPage();
    }
  }

  function initWishlist() {
    updateWishlistButtons();
    updateWishlistCount();
    renderWishlistPage();
    document.addEventListener('click', handleDocumentClick, true);
    window.addEventListener('storage', (event) => {
      if (event.key === STORAGE_KEY) {
        updateWishlistButtons();
        updateWishlistCount();
        renderWishlistPage();
      }
    });
  }

  window.AviWishlist = {
    getWishlist,
    saveWishlist,
    addToWishlist,
    removeFromWishlist,
    toggleWishlist,
    isInWishlist,
    updateWishlistButtons,
    updateWishlistCount,
    renderWishlistPage,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWishlist);
  } else {
    initWishlist();
  }
})();
